import { useCallback, useMemo } from 'react';
import type { ActiveDocumentInfo } from '../../../shared/hooks/useActiveDocumentName';
import { resolveConnectorDocumentIdentity } from '../../../shared/lib/connectorTarget';
import { useConfigurationStore } from '../../../shared/stores/useConfigurationStore';
import { usePcfValidationStore } from '../../../shared/stores/usePcfValidationStore';
import type { ValidationWorkspaceTab } from '../../../shared/stores/usePcfValidationStore';
import { useVerifiedSetupBaseline } from '../../../shared/stores/useUIStore';
import type { PipingSystem } from '../../export-pcf';
import {
  collectFocusedElementIds,
  collectFocusedSystemNames,
} from '../lib/mappingPageProjectProfileUtils';
import { workspaceTabDestination } from '../lib/mappingNavigationDestinations';
import type { MappingWorkspaceTabIndex } from '../lib/mappingWorkspacePageModel';
import {
  canDiscardToVerifiedBaseline,
  discardToVerifiedBaseline,
  resolveWorkspaceBaselineRestore,
} from '../lib/verifiedBaselineDiscard';
import { useMappingWorkspaceNavigation } from './useMappingWorkspaceNavigation';
import type { useMappingProjectReadiness } from './useMappingProjectReadiness';
import type { MappingWorkspaceFocusRefs } from './useMappingWorkspaceFocusRefs';
import { useWorkspaceSaveActions } from './useWorkspaceSaveActions';

type MappingReadiness = ReturnType<typeof useMappingProjectReadiness>;
type SetFocusedSystem = ReturnType<typeof usePcfValidationStore.getState>['setFocusedSystem'];

interface UseMappingWorkspaceNavigationControllerArgs {
  focusRefs: MappingWorkspaceFocusRefs;
  isQuickFillWorkspaceTab: boolean;
  activeTab: MappingWorkspaceTabIndex;
  setActiveTab: (tab: MappingWorkspaceTabIndex) => void;
  requestedWorkspaceTab: ValidationWorkspaceTab | null;
  clearRequestedWorkspaceTab: () => void;
  activeDocumentInfo: ActiveDocumentInfo;
  projectIdentityPending: boolean;
  projectProfileGateBlocksActions: boolean;
  mappingReadiness: MappingReadiness;
  quickFillHasPendingChanges: boolean;
  currentSettingsNeedProfileSave: boolean;
  setSaveError: (error: string | null) => void;
  handleApply: () => Promise<boolean>;
  handleContinue: () => Promise<boolean>;
  onLoadProfile: (profileKey?: string) => Promise<boolean>;
  closeWorkspaceOverlays: () => void;
  onGoBack: () => void;
  setFocusedSystem: SetFocusedSystem;
}

export const useMappingWorkspaceNavigationController = ({
  focusRefs,
  isQuickFillWorkspaceTab,
  activeTab,
  setActiveTab,
  requestedWorkspaceTab,
  clearRequestedWorkspaceTab,
  activeDocumentInfo,
  projectIdentityPending,
  projectProfileGateBlocksActions,
  mappingReadiness,
  quickFillHasPendingChanges,
  currentSettingsNeedProfileSave,
  setSaveError,
  handleApply,
  handleContinue,
  onLoadProfile,
  closeWorkspaceOverlays,
  onGoBack,
  setFocusedSystem,
}: UseMappingWorkspaceNavigationControllerArgs) => {
  const verifiedSetupBaseline = useVerifiedSetupBaseline();
  const setupIdentity = useMemo(
    () =>
      resolveConnectorDocumentIdentity({
        projectKey: activeDocumentInfo.projectKey,
        documentName: activeDocumentInfo.documentName,
        documentPath: activeDocumentInfo.documentPath,
      }),
    [
      activeDocumentInfo.projectKey,
      activeDocumentInfo.documentName,
      activeDocumentInfo.documentPath,
    ],
  );
  const baselineRestore = useMemo(
    () => resolveWorkspaceBaselineRestore(verifiedSetupBaseline, setupIdentity),
    [setupIdentity, verifiedSetupBaseline],
  );

  const { handleWorkspaceApply, handleUnsavedChangesSave, handleWorkspaceContinue } = useWorkspaceSaveActions({
    focusRefs,
    isQuickFillWorkspaceTab,
    activeTab,
    projectIdentityPending,
    projectProfileGateBlocksActions,
    mappingReadiness,
    quickFillHasPendingChanges,
    setSaveError,
    handleApply,
    handleContinue,
  });

  // The guard's save must bypass the profile-gate block: the gate's own
  // auto-confirm raised this dialog, so requiring a confirmed profile here
  // would deadlock the startup profile load.
  const handleSaveUnsavedTabChanges = useCallback(
    () => handleUnsavedChangesSave(),
    [handleUnsavedChangesSave],
  );
  const handleDiscardUnsavedTabChanges = useCallback(
    async () =>
      discardToVerifiedBaseline({
        restore: baselineRestore,
        loadProfile: onLoadProfile,
        isDirty: () => useConfigurationStore.getState().isDirty,
      }),
    [baselineRestore, onLoadProfile],
  );
  const navigation = useMappingWorkspaceNavigation({
    activeTab,
    setActiveTab,
    requestedWorkspaceTab,
    clearRequestedWorkspaceTab,
    activeProjectKey: activeDocumentInfo.projectKey,
    isDirty: Boolean(currentSettingsNeedProfileSave),
    hasVerifiedBaseline: canDiscardToVerifiedBaseline(baselineRestore),
    onSave: handleSaveUnsavedTabChanges,
    onDiscard: handleDiscardUnsavedTabChanges,
    closeWorkspaceOverlays,
    onGoBack,
  });
  const {
    requestWorkspaceTabChange,
    requestNavigation,
    handleMappingBack,
    handleReturnToGuidedSetup,
    unsavedTabChangeDialog,
  } = navigation;

  const handleSelectWorkspaceTab = useCallback(
    (tab: MappingWorkspaceTabIndex) => requestWorkspaceTabChange(tab, 'embeddedTab'),
    [requestWorkspaceTabChange],
  );
  const handleOpenQuickFill = useCallback(
    () => requestWorkspaceTabChange(1, 'programmaticTab'),
    [requestWorkspaceTabChange],
  );
  const handleOpenValidationForSystem = useCallback(
    (system: PipingSystem) => {
      void requestNavigation(workspaceTabDestination(2, 'validationTarget'), () => {
        setFocusedSystem({
          name: system.systemName,
          elementIds: collectFocusedElementIds(system),
          systemNames: collectFocusedSystemNames(system),
        });
        setActiveTab(2);
      });
    },
    [requestNavigation, setActiveTab, setFocusedSystem],
  );

  return {
    handleWorkspaceApply,
    handleWorkspaceContinue,
    requestWorkspaceTabChange,
    handleMappingBack,
    handleReturnToGuidedSetup,
    unsavedTabChangeDialog,
    handleSelectWorkspaceTab,
    handleOpenQuickFill,
    handleOpenValidationForSystem,
  };
};
